// UserAvatar — a person's avatar with a deterministic coloured fallback.
//
// Stride serves cached avatar bytes from GET /api/avatars/:accountId, which
// 404s for anyone who has no bytes cached (every Stride-native account, and
// plenty of migrated ones). The old comment header rendered a bare initial on
// a single fixed background, so every author looked identical — you couldn't
// tell one commenter from another at a glance.
//
// Here the <img> is attempted first and ANY load failure falls back to initials
// on a colour picked by hashing the account id (or, failing that, the display
// name). Same person → same colour everywhere in the app, forever; different
// people → almost always a different colour.

import { useEffect, useMemo, useState } from 'react';
import '@platform/ui/UserAvatar/UserAvatar.css';

// Ten hand-picked hues that stay legible against white text in both themes.
// Deliberately not the status/priority colours — an avatar must never read as
// a state badge.
const AVATAR_COLORS = [
  '#0b6bcb', '#7c3aed', '#b42318', '#0e7490', '#a16207',
  '#15803d', '#be185d', '#4338ca', '#c2410c', '#0f766e',
];

// FNV-1a — small, stable, and no collisions worth worrying about across the
// handful of people on one work item.
function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function avatarColor(seed) {
  if (!seed) return AVATAR_COLORS[0];
  return AVATAR_COLORS[hashString(String(seed)) % AVATAR_COLORS.length];
}

// "Thandi Operator" -> "TO", "zebra-bot" -> "Z", "" -> "?"
export function avatarInitials(displayName) {
  const name = (displayName || '').trim();
  if (!name) return '?';
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export default function UserAvatar({
  user,
  size = 32,
  className = '',
  resolveImageUrl,
  title,
}) {
  const [failed, setFailed] = useState(false);

  // Two shapes arrive here: a Jira-shaped user (displayName + avatarUrls —
  // comment authors, assignees, history) and the session user from
  // AuthContext (name + picture — "Add a comment…", the account menu).
  // Read both so the signed-in person looks the same as their own comments.
  const accountId = user?.accountId || null;
  const displayName = user?.displayName || user?.name || null;
  const rawSrc = user?.avatarUrls?.['48x48']
    || user?.avatarUrls?.['32x32']
    || user?.avatarUrls?.['24x24']
    || user?.picture
    || null;
  const src = rawSrc && typeof resolveImageUrl === 'function' ? resolveImageUrl(rawSrc) : rawSrc;

  // A new person in the same slot must get a fresh attempt at their image.
  useEffect(() => { setFailed(false); }, [src]);

  const color = useMemo(() => avatarColor(accountId || displayName), [accountId, displayName]);
  const label = title || displayName || 'Unknown user';
  const dimension = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        className={`user-avatar ${className}`}
        style={dimension}
        src={src}
        alt=""
        title={label}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className={`user-avatar user-avatar--initials ${className}`}
      style={{ ...dimension, background: color, fontSize: Math.max(9, Math.round(size * 0.42)) }}
      title={label}
      aria-hidden="true"
    >
      {avatarInitials(displayName)}
    </span>
  );
}
